import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardBody, CardFooter, CardHeader, Col, Row } from "react-bootstrap";
import { Field, Formik, FormikProps } from "formik";
import * as yup from "yup";
import { find, findIndex, get, isEmpty } from "lodash";
import { Button } from "primereact/button";
import { InputField } from "../../../../../components/Shared/InputField";
import InputAttachment from "../../../../../components/Shared/InputAttachment";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

interface IProps {
    dropdowns?: any | undefined;
    providerUploadSlug?: string | null;
}

const validationSchema = (t: Function): any => {
    return yup.object().shape({
        title: yup.string().required(t('the field is required.', { field: t('title') })).min(3).nullable(),
        document_id: yup.string().required(t('please upload the document')).nullable(),
    });
};

type TInputForm = {
    title: null | string,
    document_id: null | string,
};

const DEFAULT_FORM_STATE = {
    title: null,
    document_id: null,
};

// @ts-ignore
const AlpOutboundLoadDocument = forwardRef((props: IProps, ref) => {
    const { providerUploadSlug } = props;
    const formRef: any = useRef();
    const [initFormState, setInitFormState] = useState<any>(DEFAULT_FORM_STATE);
    const [documents, setDocuments] = useState([]);
    const { t }: any = useTranslation();

    // Expose documents to parent component
    useImperativeHandle(ref, () => ({
        getDocuments: () => documents,
        resetDocuments: () => {
            setDocuments([]);
            setInitFormState(DEFAULT_FORM_STATE);
        }
    }));

    const addDocument = async (payloads: TInputForm, { setErrors, resetForm }: any) => {
        let _documents: any = [...documents];
        let existingDocument = find(_documents, { document_id: payloads.document_id });

        if ( !isEmpty(existingDocument) ) {
            const index = findIndex(_documents, { document_id: payloads.document_id });
            existingDocument = payloads;
            _documents[index] = existingDocument;
        } else {
            _documents.push(payloads);
        }

        setDocuments(_documents);
        resetForm();
        setInitFormState(DEFAULT_FORM_STATE);
    };

    const onDelete = (documentId: any) => {
        if ( find(documents, { document_id: documentId }) ) {
            // @ts-ignore
            const trashIndex = findIndex(documents, { document_id: documentId });
            const _documents = documents.filter((_: any, index: number) => ( index != trashIndex ));
            setDocuments(_documents);
        }
    };

    const onEdit = (item: any) => {
        setInitFormState(item);
    };

    const onAfterUpload = (name: any, size: any) => {
        const base: any = get(formRef.current, ['values']);

        setInitFormState({
            ...base,
            title: name,
            size: size
        })
    };

    const onResetForm = () => {
        const base: any = formRef.current;
        if ( base ) {
            base.resetForm();
        }
        setInitFormState(DEFAULT_FORM_STATE);
    };

    const actionBodyTemplate = (rowData: any) => {
        return (
            <React.Fragment>
                <a className={ 'me-2' } onClick={ () => onEdit(rowData) }>
                    <i className={ 'fas fa-edit' } />
                </a>
                <a
                    className={ 'me-2' }
                    href={ `${ process.env.REACT_APP_DOCUMENT_URL }/${ rowData.document_id }` }
                    target='_blank'
                    rel="noopener noreferrer">
                    <i className={ 'fas fa-download' } />
                </a>
                <a onClick={ () => onDelete(rowData.document_id) }>
                    <i className={ 'fas fa-trash' } />
                </a>
            </React.Fragment>
        );
    };

    return (
        <Formik
            innerRef={ formRef }
            enableReinitialize={ true }
            initialValues={ initFormState }
            onSubmit={ addDocument }
            validationSchema={ validationSchema(t) }
        >
            { (formProps: FormikProps<TInputForm>) => {
                return (
                    <Card className={ "border-secondary mb-5 pt-2" }>
                        <CardHeader className={ "bg-body-tertiary p-2" }>
                            <h4 className="card-title m-0">{ t('documents') }</h4>
                        </CardHeader>
                        <CardBody>
                            <Row>
                                <Col sm={ 12 }>
                                    <Field component={ InputField }
                                           name="title"
                                           label={ t("title") }
                                           placeholder={ t("enter") + ' ' + t("title").toLowerCase() }
                                           isRequired />
                                </Col>
                                <Col sm={ 12 }>
                                    <Row>
                                        <Col sm={ 12 } md={ 8 }>
                                            <Field component={ InputAttachment }
                                                   name="document_id"
                                                   label={ t("document") }
                                                   uploadToAlp={ 1 }
                                                   providerUploadSlug={ providerUploadSlug ?? undefined }
                                                   onAfterUpload={ onAfterUpload }
                                                   isRequired
                                            />
                                        </Col>
                                        <Col sm={ 12 } md={ 4 } className={ "text-end" }>
                                            <div><label className="text-g-800 form-label">&nbsp;</label></div>
                                            <Button type="button" size={ "small" }
                                                    disabled={ formProps.isSubmitting }
                                                    onClick={ () => formProps.submitForm() }
                                                    title={ t("to data save into database") }
                                                    className="btn btn-success waves-effect waves-light">
                                                <i className="fas fa-plus" />
                                            </Button>
                                            <Button type="button" size={ "small" }
                                                    disabled={ formProps.isSubmitting }
                                                    onClick={ onResetForm }
                                                    title={ t("to make all fields empty") }
                                                    className="btn btn-warning waves-effect waves-light ms-2">
                                                <i className="fas fa-sync" />
                                            </Button>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </CardBody>
                        <CardFooter className={ "mt-2" }>
                            <DataTable value={ documents } editMode="row" dataKey="document_id">
                                <Column field="title" header={ t('title') } />
                                <Column body={ actionBodyTemplate } headerStyle={ { width: '10%', minWidth: '8rem' } }
                                        bodyStyle={ { textAlign: 'center' } } />
                            </DataTable>
                        </CardFooter>
                    </Card>
                );
            } }
        </Formik>
    );
});

export default AlpOutboundLoadDocument;

